import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import BrandLogosStrip from '@/pages/home/components/BrandLogosStrip';

function useVisible(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const pillars = [
  {
    num: '01',
    icon: 'ri-settings-4-line',
    title: 'Customized Management Solutions',
    body: 'Tailored to each owner\'s priorities, investment goals, and contract structure. Personalized attention with the operational power of an experienced organization.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
  },
  {
    num: '02',
    icon: 'ri-loop-right-line',
    title: 'Asset Lifecycle Support',
    body: 'From acquisition and feasibility analysis through pre-opening, stabilization, and exit strategy — we support your investment at every stage.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  {
    num: '03',
    icon: 'ri-shake-hands-line',
    title: 'Ownership-Aligned Mindset',
    body: 'Full transparency, strategic insight, and proactive risk management in every relationship. With 360, you\'re not just another property in a portfolio — you\'re a priority.',
    color: 'text-sky-400',
    bg: 'bg-sky-500/10',
  },
  {
    num: '04',
    icon: 'ri-tools-line',
    title: 'Brand Negotiation & PIP Management',
    body: 'We handle brand negotiations, Property Improvement Plan evaluations, and renovation oversight — ensuring capital is deployed efficiently and brand compliance is maintained.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
  },
  {
    num: '05',
    icon: 'ri-bar-chart-2-line',
    title: 'Feasibility & Underwriting',
    body: 'Competitive benchmarking, pro forma modeling, and market analysis to guide smart decisions for acquisitions, repositionings, and brand conversions.',
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
  },
  {
    num: '06',
    icon: 'ri-bank-line',
    title: 'Access to Capital Partners',
    body: 'Connections to trusted equity and financing partners to support development or repositioning when needed.',
    color: 'text-teal-400',
    bg: 'bg-teal-500/10',
  },
];

const contacts = [
  {
    name: 'Marcus J. Reynolds',
    title: 'Vice President, Business Development',
    phone: '(321) 555-0182',
    email: 'mreynolds@360hospitalitygroup.com',
    bio: 'Marcus brings 18 years of hotel development and owner relations experience across IHG, Marriott, and independent brands throughout the Southeast. He specializes in new management agreements, brand conversions, and portfolio acquisitions.',
    image: 'https://readdy.ai/api/search-image?query=Professional%20male%20hospitality%20executive%20headshot%2C%20confident%20smile%2C%20business%20suit%2C%20neutral%20light%20gray%20background%2C%20studio%20portrait%20photography%2C%20mid%2040s%2C%20polished%20corporate%20appearance%2C%20warm%20amber%20lighting%2C%20sharp%20focus%2C%20clean%20professional%20look&width=400&height=400&seq=bd-contact-1-360&orientation=squarish',
    linkedin: '#',
  },
  {
    name: 'Diana L. Castillo',
    title: 'Director, Owner Relations',
    phone: '(321) 555-0247',
    email: 'dcastillo@360hospitalitygroup.com',
    bio: 'Diana has spent 14 years building owner relationships across Florida\'s hospitality market, with deep expertise in asset management, performance reporting, and long-term partnership strategy. She is the primary point of contact for all active ownership groups.',
    image: 'https://readdy.ai/api/search-image?query=Professional%20female%20hospitality%20executive%20headshot%2C%20warm%20confident%20smile%2C%20business%20attire%2C%20neutral%20light%20background%2C%20studio%20portrait%20photography%2C%20mid%2030s%2C%20polished%20corporate%20appearance%2C%20natural%20lighting%2C%20sharp%20focus%2C%20clean%20professional%20look&width=400&height=400&seq=bd-contact-2-360&orientation=squarish',
    linkedin: '#',
  },
  {
    name: 'Trevor A. Nguyen',
    title: 'VP, Revenue Strategy',
    phone: '(321) 555-0319',
    email: 'tnguyen@360hospitalitygroup.com',
    bio: 'Trevor leads revenue strategy across the 360 portfolio, with 12 years of experience in dynamic pricing, OTA optimization, and demand forecasting for branded and independent hotels. He has driven RevPAR index growth across every property he has managed.',
    image: 'https://readdy.ai/api/search-image?query=Professional%20male%20hospitality%20executive%20headshot%2C%20confident%20professional%20smile%2C%20business%20suit%2C%20neutral%20clean%20background%2C%20studio%20portrait%20photography%2C%20early%2040s%2C%20polished%20corporate%20appearance%2C%20warm%20natural%20lighting%2C%20sharp%20focus%2C%20clean%20professional%20look&width=400&height=400&seq=bd-contact-3-360&orientation=squarish',
    linkedin: '#',
  },
];

const lifecycleSteps = [
  { icon: 'ri-search-eye-line', label: 'Acquisition & Feasibility' },
  { icon: 'ri-hammer-line', label: 'Pre-Opening & Development' },
  { icon: 'ri-rocket-line', label: 'Launch & Stabilization' },
  { icon: 'ri-line-chart-line', label: 'Performance Optimization' },
  { icon: 'ri-funds-line', label: 'Asset Repositioning' },
  { icon: 'ri-door-open-line', label: 'Exit Strategy & Sale' },
];

function PartnerHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative h-[560px] md:h-[660px] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Two%20hotel%20executives%20shaking%20hands%20in%20a%20modern%20luxury%20hotel%20lobby%2C%20professional%20business%20partnership%20meeting%2C%20warm%20amber%20lighting%2C%20marble%20floors%2C%20sophisticated%20interior%20design%2C%20Florida%20upscale%20hotel%20setting%2C%20editorial%20photography%2C%20genuine%20professional%20connection%2C%20wide%20angle%2C%20clean%20background%2C%20premium%20corporate%20atmosphere&width=1920&height=900&seq=partner-hero-360hg&orientation=landscape"
          alt="Partner With 360 Hospitality Group"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/15"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/50 to-transparent"></div>
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pb-16 md:pb-24">
        <div className={`max-w-3xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-400"></div>
            <span className="text-xs font-medium text-amber-400 uppercase tracking-[0.25em]">Partner With Us</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-light text-white leading-tight tracking-tight mb-5">
            One Great Partnership<br />
            <span className="text-amber-400 italic">Is All You Need</span>
          </h1>
          <p className="text-lg text-gray-300 font-light leading-relaxed max-w-2xl">
            Partner with a management company that puts your priorities first — where your investment goals drive our strategy, and your property's success is our shared mission.
          </p>
        </div>
      </div>
    </section>
  );
}

function PartnerIntro() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-20 bg-white border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <div className={`lg:col-span-3 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="h-px w-8 bg-amber-500"></div>
              <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">The 360 Difference</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-slate-900 leading-tight mb-7">
              Experienced Enough.<br />
              <span className="text-amber-500 italic">Personal Enough.</span>
            </h2>
            <p className="text-base text-slate-600 font-light leading-relaxed">
              At 360 Hospitality Group, we strike the ideal balance: experienced enough to deliver operational excellence, economies of scale, and brand leverage — yet personal enough to give each owner hands-on attention, tailored solutions, and transparent communication. Your investment goals drive our strategy, and your property's success is our shared mission.
            </p>
          </div>
          <div className={`lg:col-span-2 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="space-y-4">
              {[
                { icon: 'ri-user-star-line', label: 'Named Senior Partner', desc: 'Direct access — not a support queue' },
                { icon: 'ri-file-chart-line', label: 'Weekly Performance Reports', desc: 'Full transparency, every step' },
                { icon: 'ri-trophy-line', label: 'Performance-Based Fees', desc: 'Our compensation tied to your results' },
                { icon: 'ri-map-pin-2-line', label: 'Florida-Native Expertise', desc: 'Every market, every demand driver' },
              ].map((item, i) => (
                <div key={i} className="flex items-center space-x-4 p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-amber-200 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                    <i className={`${item.icon} text-lg text-amber-600`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{item.label}</p>
                    <p className="text-xs text-slate-500 font-light">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValuePillars() {
  const { ref, visible } = useVisible();
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">What You Get</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl font-light text-white leading-tight">
              Six Reasons Owners<br />
              <span className="text-amber-400 italic">Choose 360</span>
            </h2>
            <p className="text-base text-gray-400 font-light max-w-sm leading-relaxed">
              Every partnership is built around your goals — not a one-size-fits-all management template.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-800/40">
          {pillars.map((p, i) => (
            <div
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className={`relative bg-slate-950 p-8 transition-all duration-500 cursor-default ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              } ${hovered === i ? 'bg-slate-900/80' : ''}`}
              style={{ transitionDelay: `${Math.min(i * 70, 350)}ms` }}
            >
              <span className="text-xs font-medium text-amber-500/50 tracking-[0.3em] mb-5 block">{p.num}</span>
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 transition-all duration-300 ${p.bg}`}>
                <i className={`${p.icon} text-2xl ${p.color}`}></i>
              </div>
              <h3 className={`text-lg font-medium mb-3 transition-colors duration-300 ${hovered === i ? p.color : 'text-white'}`}>
                {p.title}
              </h3>
              <p className="text-sm text-gray-400 font-light leading-relaxed">{p.body}</p>
              <div className={`absolute bottom-0 left-0 h-0.5 transition-all duration-500 ${p.color.replace('text-', 'bg-')} ${hovered === i ? 'w-full' : 'w-0'}`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AssetLifecycle() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-20 bg-amber-500">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-xs font-medium text-white/70 uppercase tracking-[0.25em] mb-3">Full Lifecycle Coverage</p>
          <h2 className="text-3xl md:text-4xl font-light text-white leading-tight">
            We're With You at Every Stage
          </h2>
        </div>
        <div className="relative">
          {/* Connector line */}
          <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-white/30 z-0" style={{ top: '2rem' }}></div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6 relative z-10">
            {lifecycleSteps.map((step, i) => (
              <div
                key={i}
                className={`flex flex-col items-center text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mb-4 shrink-0">
                  <i className={`${step.icon} text-2xl text-amber-600`}></i>
                </div>
                <p className="text-xs font-medium text-white uppercase tracking-wide leading-tight">{step.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}



function PartnerForm() {
  const { ref, visible } = useVisible();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value ?? '';
    if (message.length > 500) return;
    setSubmitting(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    await fetch('https://readdy.ai/api/form/d79qcp1rcamcd36dblgg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    });
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left — copy */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="h-px w-8 bg-amber-500"></div>
              <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Start the Conversation</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-white leading-tight mb-6">
              Let's Work<br />
              <span className="text-amber-400 italic">Together</span>
            </h2>
            <p className="text-base text-gray-400 font-light leading-relaxed mb-10">
              Tell us about your property and your goals. A 360 senior partner will respond within 24 hours — no pitch deck, no junior rep, no runaround.
            </p>

            {/* Guarantees */}
            <div className="space-y-4">
              {[
                { icon: 'ri-time-line', text: '24-hour response from a senior partner' },
                { icon: 'ri-lock-line', text: 'Your information is never shared or sold' },
                { icon: 'ri-checkbox-circle-line', text: 'No commitment required to start the conversation' },
                { icon: 'ri-map-pin-2-line', text: 'Florida-specific insight from day one' },
              ].map((g, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-500/10 rounded-lg shrink-0">
                    <i className={`${g.icon} text-amber-400 text-sm`}></i>
                  </div>
                  <p className="text-sm text-gray-400 font-light">{g.text}</p>
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="mt-10 pt-10 border-t border-slate-800">
              <p className="text-xs text-gray-500 font-light mb-3 uppercase tracking-widest">Or reach us directly</p>
              <div className="space-y-2">
                <a href="tel:3213368093" className="flex items-center space-x-2 text-sm text-gray-400 hover:text-amber-400 transition-colors">
                  <i className="ri-phone-line text-amber-500 text-base"></i>
                  <span className="font-light">321-336-8093</span>
                </a>
                <a href="mailto:info@360hospitalitygroup.com" className="flex items-center space-x-2 text-sm text-gray-400 hover:text-amber-400 transition-colors">
                  <i className="ri-mail-line text-amber-500 text-base"></i>
                  <span className="font-light">info@360hospitalitygroup.com</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-white rounded-2xl p-8 md:p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-5">
                    <i className="ri-check-line text-3xl text-amber-600"></i>
                  </div>
                  <h3 className="text-2xl font-light text-slate-900 mb-3">Message Received</h3>
                  <p className="text-sm text-slate-500 font-light leading-relaxed max-w-xs mx-auto">
                    A 360 senior partner will be in touch within 24 hours. We look forward to learning about your property.
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center space-x-2 mt-8 text-sm font-medium text-amber-600 hover:text-amber-500 transition-colors"
                  >
                    <i className="ri-arrow-left-line"></i>
                    <span>Back to Homepage</span>
                  </Link>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-light text-slate-900 mb-1">Let's Work Together</h3>
                  <p className="text-sm text-slate-500 font-light mb-8">Tell us about your property and goals.</p>

                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">Full Name *</label>
                      <input
                        name="full_name"
                        required
                        type="text"
                        placeholder="John Smith"
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>

                    {/* Email + Phone */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">Email *</label>
                        <input
                          name="email"
                          required
                          type="email"
                          placeholder="john@hotel.com"
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">Phone</label>
                        <input
                          name="phone"
                          type="tel"
                          placeholder="(321) 000-0000"
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors"
                        />
                      </div>
                    </div>

                    {/* Property Name + Rooms */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">Property Name *</label>
                        <input
                          name="property_name"
                          required
                          type="text"
                          placeholder="e.g. Sunset Inn, Orlando"
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5 uppercase tracking-wide">Number of Rooms</label>
                        <input
                          name="num_rooms"
                          type="number"
                          min="1"
                          placeholder="e.g. 85"
                          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-medium text-slate-600 uppercase tracking-wide">Message</label>
                        <span className={`text-xs font-light ${charCount > 480 ? 'text-rose-500' : 'text-slate-400'}`}>
                          {charCount}/500
                        </span>
                      </div>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        placeholder="Tell us about your property, current challenges, and what you're looking for in a management partner..."
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 disabled:opacity-60 text-white font-medium text-sm tracking-wide transition-all whitespace-nowrap cursor-pointer flex items-center justify-center space-x-2"
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base"></i>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <span>Send Message</span>
                          <i className="ri-arrow-right-line text-base"></i>
                        </>
                      )}
                    </button>

                    <p className="text-xs text-slate-400 font-light text-center">
                      By submitting, you agree to our{' '}
                      <Link to="/privacy" className="text-amber-600 hover:underline">Privacy Policy</Link>.
                      We respond within 24 hours.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PartnerTestimonial() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center space-x-1 mb-8">
            {[...Array(5)].map((_, i) => (
              <i key={i} className="ri-star-fill text-amber-400 text-xl"></i>
            ))}
          </div>
          <blockquote className="text-2xl md:text-3xl font-light text-slate-900 leading-relaxed mb-8 italic">
            "We'd been through two operators who charged the same fee no matter what happened. 360 hit our RevPAR target in the first quarter. First time an operator actually felt like a partner."
          </blockquote>
          <div className="flex items-center justify-center space-x-3">
            <div className="w-10 h-px bg-slate-300"></div>
            <div>
              <p className="text-sm font-medium text-slate-900">Robert K.</p>
              <p className="text-xs text-slate-500 font-light">Hotel Owner — Fort Myers, FL</p>
            </div>
            <div className="w-10 h-px bg-slate-300"></div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PartnerPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <PartnerHero />
      <PartnerIntro />
      <BrandLogosStrip />
      <ValuePillars />
      <AssetLifecycle />
      <PartnerTestimonial />
      <PartnerForm />
      <Footer />
    </div>
  );
}
